<template>

<div class="print-summary">

	<div class="print-summary-overlay" v-on:click="$emit('close')"></div>

	<div class="print-summary-panel">

		<div class="print-summary-header print-hide">

			<div class="print-summary-title">My Bone Health Record</div>

			<button class="print-summary-close" v-on:click="$emit('close')">&times;</button>

		</div>

		<div class="print-summary-body" ref="printContent">

			<div class="print-summary-print-title print-only">My Bone Health Record</div>

			<com-risk :section="recordSection" v-if="recordSection" />

			<com-why :section="recordSection" v-if="recordSection" />

			<com-improve :section="improveSection" v-if="improveSection" />

			<com-treatment :section="recordSection" v-if="recordSection && showTreatmentDetail && hasTreatmentData" />

			<div class="print-summary-section">

				<div class="print-summary-section-title">Next Steps</div>

				<div class="print-summary-group" v-if="medicineSummary">
					<div class="print-summary-group-title">Medicine</div>
					<div class="print-summary-group-text">{{ medicineSummary }}</div>
				</div>

				<div class="print-summary-group" v-if="referralSummary">
					<div class="print-summary-group-title">Referral</div>
					<div class="print-summary-group-text">{{ referralSummary }}</div>
					<div class="print-summary-group-text" v-if="referralForSummary">{{ referralForSummary }}</div>
				</div>

				<div class="print-summary-group" v-if="patientActions.length">
					<div class="print-summary-group-title">Patient</div>
					<div class="print-summary-group-text" v-for="(action, index) in patientActions" :key="index">{{ action }}</div>
				</div>

			</div>

			<div class="print-summary-section print-summary-info" v-if="showBisphosphonateInfo">
				<p>Your healthcare professional has recommended an oral bisphosphonate. Oral bisphosphonates are the most commonly prescribed medicine for osteoporosis. They work by slowing down the cells that break down bone, allowing the bone-building cells to work more effectively. This helps to strengthen your bones and reduce the risk of breaking a bone.</p>
			</div>

			<div class="print-summary-footer-text">
				For more information please visit <a href="https://osteoporosisoptions.co.uk/patients-public/" target="_blank">Osteoporosis Options</a> and the <a href="https://theros.org.uk/information-and-support/osteoporosis/treatment/" target="_blank">Royal Osteoporosis Society</a>.
			</div>

		</div>

		<div class="print-summary-footer print-hide">

			<button class="print-summary-button print-summary-button-cancel" v-on:click="$emit('close')">Close</button>

			<button class="print-summary-button print-summary-button-primary" v-on:click="onPrintClick"><i class="fa-solid fa-print"></i> Print</button>

		</div>

	</div>

</div>

</template>

<script>

import comRisk from './../../../tool/aid/record/summary/Risk'
import comWhy from './../../../tool/aid/record/summary/Why'
import comImprove from './../../../tool/aid/record/summary/Improve'
import comTreatment from './../../../tool/aid/record/summary/Treatment'

export default {

	props: ['recommendation'],

	components: {

		'com-risk': comRisk,
		'com-why': comWhy,
		'com-improve': comImprove,
		'com-treatment': comTreatment

	},

	computed: {

		recordSection: function() {

			return this.$_.findWhere(this.$store.getters['data/aid/sections'], {
				type: 'record'
			})

		},

		improveSection: function() {

			return this.recordSection

		},

		medicineVariable: function() {

			return this.$store.getters['session/values'][100]

		},

		medicineSummary: function() {

			var options = {
				1: 'Consider starting an oral bisphosphonate',
				2: 'Consider starting hormone replacement therapy (HRT)',
				3: 'No drug recommended',
				4: 'Continue with an oral bisphosphonate',
				5: 'Stop current treatment with an oral bisphosphonate',
				6: 'Stop current treatment with an oral bisphosphonate and consider an alternative drug in primary care'
			}

			return options[this.medicineVariable] || null

		},

		referralSummary: function() {

			var value = this.$store.getters['session/values'][101]

			if (value === 1) return 'Onwards referral for opinion or scans'
			if (value === 2) return 'No onwards referral'

			return null

		},

		referralForSummary: function() {

			var value = this.$store.getters['session/values'][102]

			if (!value || !value.length) return null

			var options = { 1: 'Bone density scan', 2: 'Specialist advice and guidance', 3: 'Specialist referral' }
			var items = []

			this.$_.each(value, function(v) {
				if (options[v]) items.push(options[v])
			})

			return items.length ? 'Referral for: ' + items.join(', ') : null

		},

		patientActions: function() {

			var actions = []
			var nextSteps = this.$store.getters['session/values'][103] || []
			var awaitItems = this.$store.getters['session/values'][105] || []
			var bookItems = this.$store.getters['session/values'][106] || []

			if (this.$_.contains(nextSteps, 1)) {
				actions.push('No further action needed \u2013 unless any further concerns')
			}

			if (this.$_.contains(nextSteps, 2)) {
				actions.push('Collect prescription for new osteoporosis medicine from pharmacy')
			}

			if (this.$_.contains(nextSteps, 3) && awaitItems.length) {
				var awaitOptions = { 1: 'next appointment', 2: 'hospital appointment', 3: 'physiotherapy appointment', 4: 'blood test' }
				var awaitTexts = []
				this.$_.each(awaitItems, function(v) { if (awaitOptions[v]) awaitTexts.push(awaitOptions[v]) })
				if (awaitTexts.length) actions.push('Await: ' + awaitTexts.join(', '))
			}

			if (this.$_.contains(nextSteps, 4) && bookItems.length) {
				var bookOptions = { 1: 'next appointment', 2: 'physiotherapy appointment', 3: 'blood test', 4: 'further contact' }
				var bookTexts = []
				this.$_.each(bookItems, function(v) { if (bookOptions[v]) bookTexts.push(bookOptions[v]) })
				if (bookTexts.length) actions.push('Book: ' + bookTexts.join(', '))
			}

			return actions

		},

		showTreatmentDetail: function() {

			return this.medicineVariable === 1 || this.medicineVariable === 4

		},

		hasTreatmentData: function() {

			var section = this.$_.findWhere(this.$store.getters['data/aid/sections'], { id: 4 })

			if (!section || !section.treatment) return false

			var named = this.$store.getters['logic/variable/named']

			return named['aid.treatment'] && this.$store.getters['session/values'][named['aid.treatment']] !== null

		},

		showBisphosphonateInfo: function() {

			return this.medicineVariable === 1 || this.medicineVariable === 4

		}

	},

	methods: {

		onPrintClick: function() {

			window.print()

		}

	}

}

</script>

<style scoped>

.print-summary {
	position: fixed;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 2000;
	display: flex;
	align-items: center;
	justify-content: center;
}

.print-summary-overlay {
	position: absolute;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
}

.print-summary-panel {
	position: relative;
	background-color: #fff;
	border-radius: 16px;
	width: 90%;
	max-width: 900px;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.is-mobile .print-summary-panel {
	width: 100%;
	max-width: 100%;
	max-height: 100%;
	height: 100%;
	border-radius: 0;
}

.print-summary-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 24px 32px;
	border-bottom: 1px solid #e0e0e0;
	flex-shrink: 0;
}

.is-mobile .print-summary-header {
	padding: 16px 20px;
}

.print-summary-title {
	font-size: 20px;
	font-weight: 700;
	color: #343434;
}

.is-mobile .print-summary-title {
	font-size: 16px;
}

.print-summary-close {
	font-size: 28px;
	color: #999;
	cursor: pointer;
	line-height: 1;
	background: none;
	border: none;
}

.print-summary-close:hover {
	color: #343434;
}

.print-summary-body {
	flex: 1;
	overflow-y: auto;
	padding: 32px;
}

.is-mobile .print-summary-body {
	padding: 20px;
}

.print-summary-print-title {
	display: none;
}

.print-summary-section {
	margin-top: 20px;
	padding-top: 20px;
	border-top: 1px solid #e0e0e0;
}

.print-summary-section-title {
	font-size: 20px;
	font-weight: 600;
	color: #343434;
	text-transform: uppercase;
	margin-bottom: 32px;
}

.print-summary-group {
	margin-bottom: 16px;
}

.print-summary-group-title {
	font-size: 18px;
	font-weight: 600;
	color: #343434;
	margin-bottom: 4px;
}

.print-summary-group-text {
	font-size: 18px;
	color: #343434;
	line-height: 22px;
	font-weight: 300;
}

.print-summary-info {
	font-size: 13px;
	color: #666;
	line-height: 20px;
}

.print-summary-info p {
	margin: 0;
}

.print-summary-footer-text {
	margin-top: 24px;
	padding-top: 12px;
	border-top: 1px solid #e0e0e0;
	font-size: 12px;
	color: #888;
	line-height: 18px;
}

.print-summary-footer-text a {
	color: #343434;
}

.print-summary-footer {
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	padding: 20px 32px;
	border-top: 1px solid #e0e0e0;
	flex-shrink: 0;
}

.is-mobile .print-summary-footer {
	padding: 16px 20px;
	flex-direction: column;
}

.is-mobile .print-summary-button {
	width: 100%;
	text-align: center;
}

.print-summary-button {
	padding: 12px 32px;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 200ms ease-in-out;
}

.print-summary-button-cancel {
	background-color: #f0f0f0;
	color: #666;
	border: 1px solid #ddd;
}

.print-summary-button-cancel:hover {
	background-color: #e0e0e0;
}

.print-summary-button-primary {
	background-color: var(--primary-color);
	color: #fff;
	border: none;
}

.print-summary-button-primary:hover {
	filter: brightness(90%);
}

.print-summary-button-primary i {
	margin-right: 8px;
}

</style>

<style>

@media print {

	.print-summary-panel,
	.print-summary-panel * {
		-webkit-print-color-adjust: exact !important;
		print-color-adjust: exact !important;
	}

	body > *:not(#app) {
		display: none !important;
	}

	#app > *:not(.print-summary-panel):not([class*="portal"]) {
		display: none !important;
	}

	.tool,
	.recommendation,
	.recommendation-content {
		display: none !important;
	}

	.print-summary-overlay {
		display: none !important;
	}

	.print-hide {
		display: none !important;
	}

	.print-only {
		display: block !important;
	}

	.print-summary {
		position: static !important;
		display: block !important;
	}

	.print-summary-panel {
		position: static !important;
		width: 100% !important;
		max-width: 100% !important;
		max-height: none !important;
		border-radius: 0 !important;
		overflow: visible !important;
	}

	.print-summary-body {
		overflow: visible !important;
		padding: 0 !important;
	}

	.print-summary-print-title {
		display: block !important;
		font-size: 22px;
		font-weight: 700;
		color: #343434;
		margin-bottom: 24px;
	}

}

</style>
